import { SlimeState } from '../content/types';
import type { GameContext } from '../core/GameContext';
import { EventKind } from '../events/EventQueue';
import { CommandKind, type CommandQueue } from './CommandBuffer';

type Executor = (context: GameContext, queue: CommandQueue, index: number) => void;

const markDead = (context: GameContext, id: number, source: number, score: number): void => {
  if (!Number.isInteger(id) || id < 0 || id >= context.slimes.capacity || context.slimes.active[id] === 0 || context.slimes.isDying(id)) return;
  context.slimes.flags[id] |= SlimeState.MarkedDead;
  // 死亡来源是引爆者（另一只史莱姆），不是水滴——新循环里水滴只灌水、不致死。
  context.events.push(EventKind.EntityDied, source, id, context.slimes.definition[id], context.slimes.x[id], context.slimes.y[id], score, source >= 0 ? context.slimes.recordingEntityId[source] ?? 0 : 0, context.slimes.recordingEntityId[id]);
};
const killSlime: Executor = (context, queue, index) => markDead(context, queue.target[index], queue.source[index], queue.a[index]);
const spawnDrop: Executor = (context, queue, index) => {
  const id = context.drops.spawn(queue.x[index], queue.y[index], queue.a[index], queue.b[index], queue.definition[index]);
  context.events.push(EventKind.EntitySpawned, queue.source[index], id, queue.definition[index], queue.x[index], queue.y[index], 0, context.slimes.recordingEntityId[queue.source[index]] ?? 0, context.drops.recordingEntityId[id]);
};
const despawnDrop: Executor = (context, queue, index) => {
  const id = queue.target[index];
  if (id < 0 || id >= context.drops.capacity || context.drops.active[id] === 0) return;
  context.events.push(EventKind.EntityDespawned, queue.source[index], id, context.drops.definition[id], context.drops.x[id], context.drops.y[id], 0, 0, context.drops.recordingEntityId[id]);
  context.drops.release(id);
};
/**
 * 史莱姆是玩法层结构，满了**拒绝并计数**而不抛错——失败判定必须早于容量耗尽（Backlog T053）：
 * 实测投放速率 12/s 时容量 16 秒耗尽，而地面堆积判负要 26 秒，抛错会让玩家什么都不做先崩后输。
 * a = 横向初速，b = 落速倍率；事件 value 置 1 标记这是史莱姆而非水滴（两者共用 EntitySpawned）。
 */
const spawnSlime: Executor = (context, queue, index) => {
  const { slimes } = context;
  if (slimes.activeCount >= slimes.capacity) { context.spawn.refused += 1; return; }
  const id = slimes.spawn(queue.x[index], queue.y[index], queue.definition[index]);
  slimes.vx[id] = queue.a[index]; slimes.fallScale[id] = queue.b[index] > 0 ? queue.b[index] : 1;
  context.events.push(EventKind.EntitySpawned, -1, id, queue.definition[index], queue.x[index], queue.y[index], 1, 0, slimes.recordingEntityId[id]);
};
const despawnSlime: Executor = (context, queue, index) => {
  const id = queue.target[index];
  if (id < 0 || id >= context.slimes.capacity || context.slimes.active[id] === 0) return;
  context.events.push(EventKind.EntityDespawned, queue.source[index], id, context.slimes.definition[id], context.slimes.x[id], context.slimes.y[id], 0, 0, context.slimes.recordingEntityId[id]);
  context.slimes.release(id);
};

export class CommandProcessor {
  // 表下标即 CommandKind 值：KillSlime 0 / SpawnDrop 1 / DespawnDrop 2 / DespawnSlime 3 / EndLevel 4 / SpawnSlime 5。
  private readonly resolution: readonly (Executor | undefined)[] = [killSlime];
  private readonly lifecycle: readonly (Executor | undefined)[] = [undefined, spawnDrop, despawnDrop, despawnSlime, undefined, spawnSlime];

  flushResolution(context: GameContext): void { this.flush(context, context.commands.resolution, this.resolution); }
  flushLifecycle(context: GameContext): void { this.flush(context, context.commands.lifecycle, this.lifecycle); }

  flushEndOfStep(context: GameContext): void {
    const queue = context.commands.endOfStep;
    if (context.state.phase === 'playing' && queue.count > 0) {
      let success = true;
      for (let index = 0; index < queue.count; index += 1) {
        if (queue.kind[index] !== CommandKind.EndLevel) throw new Error(`No EndOfStep executor for command ${queue.kind[index]}`);
        if (queue.a[index] <= 0) success = false;
      }
      context.state.phase = success
        ? context.state.testMode || context.state.levelIndex < context.catalog.levels.length - 1 ? 'level-clear' : 'complete'
        : 'game-over';
      context.events.push(EventKind.LevelEnded, -1, -1, 0, 0, 0, success ? 1 : 0);
    }
    queue.clear();
  }

  private flush(context: GameContext, queue: CommandQueue, executors: readonly (Executor | undefined)[]): void {
    const count = queue.count;
    for (let index = 0; index < count; index += 1) {
      const kind = queue.kind[index]; const executor = executors[kind];
      if (executor === undefined) throw new Error(`No executor registered for command ${kind}`);
      executor(context, queue, index);
    }
    if (queue.count !== count) throw new Error('An executor appended recursively to its active command lane');
    queue.clear();
  }
}
